import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Loader2, Download, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { validateUtmUrl } from "@/lib/csv";

function IssueList({ issues, ok }: { issues: { level: "error" | "warn"; field: string; message: string }[]; ok: boolean }) {
  if (ok) return <div className="mt-2 text-xs flex items-center gap-1 text-emerald-500"><CheckCircle2 className="w-3.5 h-3.5" /> Looks good.</div>;
  if (issues.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1 text-xs">
      {issues.map((i, idx) => (
        <li key={idx} className={`flex items-start gap-1.5 ${i.level === "error" ? "text-destructive" : "text-amber-400"}`}>
          {i.level === "error" ? <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
          <span><span className="font-mono">{i.field}</span> — {i.message}</span>
        </li>
      ))}
    </ul>
  );
}

type LandingPage = { id: string; slug: string; title: string; offer_promo_code_id: string | null };
type Promo = { id: string; code: string; description: string | null };
type Campaign = { id: string; name: string };

const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60);

export default function AdminUtmBuilder() {
  const [origin, setOrigin] = useState("");
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [promos, setPromos] = useState<Promo[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  const [pageSlug, setPageSlug] = useState("");
  const [promoId, setPromoId] = useState<string>("__none");
  const [campaignId, setCampaignId] = useState<string>("__none");
  const [source, setSource] = useState("instagram");
  const [medium, setMedium] = useState("social");
  const [term, setTerm] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    setOrigin(window.location.origin);
    (async () => {
      const [{ data: lp }, { data: pc }, { data: cp }] = await Promise.all([
        supabase.from("landing_pages").select("id,slug,title,offer_promo_code_id").order("slug"),
        supabase.from("promo_codes").select("id,code,description").eq("active", true).order("code"),
        supabase.from("email_campaigns").select("id,name").order("name"),
      ]);
      setPages((lp as any) ?? []);
      setPromos((pc as any) ?? []);
      setCampaigns((cp as any) ?? []);
      if (lp?.[0]?.slug) setPageSlug(lp[0].slug);
      setLoading(false);
    })();
  }, []);

  const selectedPromo = promos.find(p => p.id === promoId);
  const selectedCampaign = campaigns.find(c => c.id === campaignId);

  const utmCampaign = useMemo(() => {
    if (selectedCampaign) return slug(selectedCampaign.name);
    if (selectedPromo) return slug(selectedPromo.code);
    return "";
  }, [selectedCampaign, selectedPromo]);

  const finalUrl = useMemo(() => {
    if (!origin || !pageSlug) return "";
    const u = new URL(`${origin}/p/${pageSlug}`);
    if (source) u.searchParams.set("utm_source", source);
    if (medium) u.searchParams.set("utm_medium", medium);
    if (utmCampaign) u.searchParams.set("utm_campaign", utmCampaign);
    if (term) u.searchParams.set("utm_term", term);
    if (content) u.searchParams.set("utm_content", content);
    if (selectedPromo) u.searchParams.set("promo", selectedPromo.code);
    return u.toString();
  }, [origin, pageSlug, source, medium, utmCampaign, term, content, selectedPromo]);

  const bulkRows = useMemo(() => {
    // generate one row per landing page x source preset
    const presets = ["instagram", "facebook", "tiktok", "email", "google", "x"];
    const out: { url: string; landing: string; source: string }[] = [];
    for (const lp of pages) {
      for (const s of presets) {
        const u = new URL(`${origin}/p/${lp.slug}`);
        u.searchParams.set("utm_source", s);
        u.searchParams.set("utm_medium", s === "email" ? "email" : "social");
        if (utmCampaign) u.searchParams.set("utm_campaign", utmCampaign);
        out.push({ url: u.toString(), landing: lp.slug, source: s });
      }
    }
    return out;
  }, [pages, origin, utmCampaign]);

  const copy = (text: string) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  const validation = useMemo(() => validateUtmUrl(finalUrl), [finalUrl]);
  const hasErrors = validation.issues.some(i => i.level === "error");

  const [checkUrl, setCheckUrl] = useState("");
  const checkResult = useMemo(() => checkUrl ? validateUtmUrl(checkUrl) : null, [checkUrl]);

  function safeCopy(text: string) {
    const r = validateUtmUrl(text);
    if (!r.ok) { toast.error(r.issues.find(i => i.level === "error")?.message ?? "Link has errors"); return; }
    if (r.issues.length > 0) toast.warning(`Copied with ${r.issues.length} warning(s).`);
    else toast.success("Copied");
    navigator.clipboard.writeText(text);
  }

  function downloadCsv() {
    const rows = [["landing_slug", "utm_source", "url"], ...bulkRows.map(r => [r.landing, r.source, r.url])];
    const csv = rows.map(r => r.map(v => /[",\n]/.test(v) ? `"${v.split('"').join('""')}"` : v).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "utm-links.csv"; a.click(); URL.revokeObjectURL(a.href);
  }

  return (
    <AdminShell title="UTM Link Builder">
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-surface border border-border p-4 space-y-3">
            <h3 className="font-display tracking-widest text-sm">SINGLE LINK</h3>
            <div>
              <Label>Landing page</Label>
              <Select value={pageSlug} onValueChange={setPageSlug}>
                <SelectTrigger><SelectValue placeholder="Pick landing page" /></SelectTrigger>
                <SelectContent>
                  {pages.map(p => <SelectItem key={p.id} value={p.slug}>/{p.slug} — {p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Campaign</Label>
                <Select value={campaignId} onValueChange={setCampaignId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">None</SelectItem>
                    {campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Promo code</Label>
                <Select value={promoId} onValueChange={setPromoId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">None</SelectItem>
                    {promos.map(p => <SelectItem key={p.id} value={p.id}>{p.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>utm_source</Label><Input value={source} onChange={e => setSource(e.target.value)} placeholder="instagram" /></div>
              <div><Label>utm_medium</Label><Input value={medium} onChange={e => setMedium(e.target.value)} placeholder="social" /></div>
              <div><Label>utm_term (optional)</Label><Input value={term} onChange={e => setTerm(e.target.value)} /></div>
              <div><Label>utm_content (optional)</Label><Input value={content} onChange={e => setContent(e.target.value)} /></div>
            </div>
            <div className="text-xs font-mono text-muted-foreground">
              utm_campaign auto-derived from campaign/promo: <strong>{utmCampaign || "(empty)"}</strong>
            </div>
            <div>
              <Label>Final URL</Label>
              <div className="flex gap-2">
                <Input readOnly value={finalUrl} />
                <Button variant="outline" onClick={() => safeCopy(finalUrl)} disabled={!finalUrl || hasErrors}><Copy className="w-4 h-4" /></Button>
              </div>
              <IssueList issues={validation.issues} ok={validation.ok && validation.issues.length === 0} />
            </div>
            <p className="text-xs text-muted-foreground">
              Tip: every landing-page visit captures the first-touch UTM into the visitor's session, then carries it through to lead capture, promo redemption, and checkout. The promo analytics page reports rollups by UTM source and landing slug.
            </p>

            <div className="border-t border-border pt-3 mt-2">
              <Label>Validate any URL</Label>
              <Input placeholder="Paste a UTM-tagged link to validate" value={checkUrl} onChange={e => setCheckUrl(e.target.value)} />
              {checkResult && <IssueList issues={checkResult.issues} ok={checkResult.ok && checkResult.issues.length === 0} />}
            </div>
          </div>

          <div className="bg-surface border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-display tracking-widest text-sm">BULK PRESET (all landing pages × common sources)</h3>
              <Button size="sm" variant="outline" onClick={downloadCsv}><Download className="w-4 h-4 mr-1" /> CSV</Button>
            </div>
            <div className="max-h-[460px] overflow-auto border border-border">
              <table className="w-full text-xs font-mono">
                <thead className="bg-panel"><tr><th className="text-left p-1.5">page</th><th className="text-left p-1.5">source</th><th className="text-left p-1.5">url</th><th></th></tr></thead>
                <tbody>
                  {bulkRows.map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="p-1.5">{r.landing}</td>
                      <td className="p-1.5">{r.source}</td>
                      <td className="p-1.5 truncate max-w-[280px]" title={r.url}>{r.url}</td>
                      <td className="p-1.5"><Button size="icon" variant="ghost" onClick={() => safeCopy(r.url)}><Copy className="w-3.5 h-3.5" /></Button></td>
                    </tr>
                  ))}
                  {bulkRows.length === 0 && <tr><td colSpan={4} className="p-4 text-muted-foreground text-center">No landing pages yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}